import { captureError, captureMessage, addSentryBreadcrumb } from './sentry';

export class ErrorHandler {
  static setupGlobalErrorHandlers() {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      
      addSentryBreadcrumb('Unhandled promise rejection', 'error', {
        reason: event.reason?.toString()
      });
      
      captureError(new Error(`Unhandled promise rejection: ${event.reason}`), {
        type: 'unhandledrejection',
        reason: event.reason
      });
    });

    // Handle global JavaScript errors
    window.addEventListener('error', (event) => {
      console.error('Global JavaScript error:', event.error);
      
      addSentryBreadcrumb('Global JavaScript error', 'error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
      
      if (event.error) {
        captureError(event.error, {
          type: 'javascript',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        });
      } else {
        captureError(new Error(event.message), {
          type: 'javascript',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        });
      }
    });

    // Handle resource loading errors
    window.addEventListener('error', (event) => {
      const target = (event as any)?.target as any;
      if (target && target !== window) {
        const tagName = target?.tagName?.toLowerCase();
        const src = target?.src || target?.href;
        
        if (src && ['img', 'script', 'link', 'audio', 'video', 'source'].includes(tagName)) {
          console.error(`Resource loading error (${tagName}):`, src);
          
          addSentryBreadcrumb('Resource loading error', 'error', {
            tagName,
            src,
            outerHTML: target?.outerHTML?.substring(0, 200)
          });
          
          captureMessage(`Failed to load ${tagName}: ${src}`, 'warning');
        }
      }
    }, true);
  }

  static handleSupabaseError(error: any, context: string, additionalData?: any) {
    console.error(`Supabase error in ${context}:`, error);
    
    addSentryBreadcrumb(`Supabase error in ${context}`, 'error', {
      message: error?.message,
      code: error?.code,
      details: error?.details,
      ...additionalData
    });
    
    captureError(new Error(`Supabase ${context}: ${error?.message || 'Unknown error'}`), {
      type: 'supabase',
      context,
      code: error?.code,
      details: error?.details,
      ...additionalData
    });
  }

  static handleAPIError(error: any, endpoint: string, method: string = 'GET') {
    console.error(`API error (${method} ${endpoint}):`, error);
    
    addSentryBreadcrumb(`API error: ${method} ${endpoint}`, 'error', {
      message: error?.message,
      status: error?.status,
      statusText: error?.statusText
    });
    
    captureError(new Error(`API ${method} ${endpoint}: ${error?.message || 'Request failed'}`), {
      type: 'api',
      endpoint,
      method,
      status: error?.status,
      statusText: error?.statusText
    });
  }

  static handleNetworkError(error: any, operation: string) {
    console.error(`Network error during ${operation}:`, error);
    
    addSentryBreadcrumb(`Network error: ${operation}`, 'error', {
      message: error?.message,
      type: error?.type
    });
    
    captureError(new Error(`Network error during ${operation}: ${error?.message || 'Network request failed'}`), {
      type: 'network',
      operation,
      errorType: error?.type
    });
  }

  static logUserAction(action: string, details?: any) {
    addSentryBreadcrumb(action, 'user', details);
  }

  static logBusinessEvent(event: string, details?: any) {
    addSentryBreadcrumb(event, 'business', details);
    
    // For critical business events, also capture as messages
    if (['load_created', 'load_accepted', 'payment_failed'].includes(event)) {
      captureMessage(`Business event: ${event}`, 'info');
    }
  }
}

// Initialize global error handlers
ErrorHandler.setupGlobalErrorHandlers();